/**
 * RU on all settings including whitelisting
 * This is in common as all apps would need it
 * Setting Key value pairs come from db of the app.
 */
import { Entity } from '../common/base/entity/entity'
import { collectionSchemas, entitySchema, methodSchemas } from './statusConfiguration.schema'
import statusConfigurationContainer from './statusConfigurationContainer'
import { ContentActions } from './statusConfigurationContentActions'
/**
 * Instructions:
 * 1. For simple usage steps:
 * a. fieldsMeta, actionsMeta, screens, sectionsMeta definition and creation of BaseEntity obj with these inputs
 * 2. For simple overrides of baseEntity
 * a. create an extended class of baseEntity and override its methods...
 * 3. For more customization
 * a. create a derived Service, derived actions and use them in derived BasedEntity class.
 * b. for component customization pass those components to the constuctor of derived BaseEntity class. SearchList component is the root component.
 *
 *
 */

class statusConfigurationEntity extends Entity {
	// override the following if needed.
	// derived actionObj
	// derived Service obj
	// getActionFns if you want to suppress the baseActionobj methods
}

const fieldsMeta = {
	statusName: {
		label: 'Status Name',
		required: false,
		type: 'text',
		name: 'statusName',
		placeholder: 'Status Name',
		create: false,
	},
	label: {
		label: 'Status Name',
		required: true,
		type: 'text',
		name: 'label',
		placeholder: 'Status Name',
		create: true,
	},
	stopSLATimer: {
		label: 'Stop SLA Timer',
		required: false,
		type: 'checkbox',
		name: 'stopSLATimer',
		isSelect: false,
		create: true,
	},
	allowTransitionFrom: {
		label: 'Allow Transition from',
		name: 'allowTransitionFrom',
		options: [],
		type: 'select',
		required: true,
		default: '',
		create: true,
	},
	transitionLabel: {
		label: 'Transition Label',
		required: true,
		type: 'text',
		name: 'transitionLabel',
		placeholder: 'Transition Label',
		create: true,
	},
	conditionalFields: {
		label: 'Conditional Fields',
		required: true,
		type: 'table',
		name: 'conditionalFields',
		create: true,
		tbody: [],
	},
	allowTransitionTo: {
		label: 'Allow Transition to',
		name: 'allowTransitionTo',
		options: [],
		type: 'select',
		required: true,
		default: '',
		create: true,
	},
	captureNote: {
		label: 'Capture notes (make notes Public / internal)',
		name: 'captureNote',
		options: [
			{
				label: 'Yes',
				value: 'yes',
			},
			{
				label: 'No',
				value: 'no',
			},
		],
		type: 'radio',
		required: true,
		create: true,
	},
	makeNotesPublicInternal: {
		label: 'Make notes Public / internal',
		name: 'makeNotesPublicInternal',
		options: [
			{
				label: 'Public',
				value: 'Public',
			},
			{
				label: 'Internal',
				value: 'internal',
			},
		],
		type: 'radio',
		required: true,
		// default: 'Public',
		create: true,
	},
	notifyRequesterOnStatusChange: {
		label: 'Notify requester on status change',
		name: 'notifyRequesterOnStatusChange',
		options: [
			{
				label: 'Yes',
				value: 'yes',
			},
			{
				label: 'No',
				value: 'no',
			},
		],
		type: 'radio',
		required: true,
		create: true,
	},
	createdBy: {
		label: 'Created By',
		name: 'createdBy',
		// readOnly: true,
		required: true,
		type: 'text',
	},
	createdAt: {
		label: 'Created At',
		name: 'createdAt',
		// readOnly: true,
		required: true,
		type: 'text',
	},
	updatedBy: {
		label: 'Last Edited By',
		name: 'updatedBy',
		// readOnly: true,
		required: true,
		type: 'text',
	},
	updatedAt: {
		label: 'Last Edited At',
		name: 'updatedAt',
		// readOnly: true,
		required: true,
		type: 'datetime-local',
	},
}

const actionsMeta = {
	save: {
		label: 'Save',
		name: 'save',
		action: 'save',
	},
	cancel: {
		label: 'Cancel',
		name: 'cancel',
		action: 'cancelEdit',
	},
	update: {
		label: 'Update',
		name: 'update',
		action: 'upsertItem',
		classes: 'fa fa-pencil fa-lg ',
		type: 'span',
	},
	list: {
		label: 'List All',
		name: 'list',
		action: 'load',
	},
	search: {
		label: 'Search',
		name: 'search',
		action: 'search',
	},
	view: {
		label: 'View',
		name: 'view',
		action: 'getItem',
		classes: 'fa fa-eye fa-lg',
		type: 'span',
	},
	createItem: {
		label: 'Create',
		action: 'upsertItem',
		name: 'create',
		type: 'button',
	},
	deleteConfig: {
		label: 'Delete',
		action: 'upsertItem',
		name: 'update',
		color: 'danger',
		type: 'button',
	},
	create: {
		label: 'Create',
		name: 'create',
		type: 'button',
		action: 'addNewItem',
	},
}

const sectionsMeta = {}

const screens = {
	list: {
		items: [
			{ name: 'statusName', type: 'link' },
			{ name: 'allowTransitionFrom', type: 'field' },
			{ name: 'allowTransitionTo', type: 'field' },
		],
		multiselect: false,
		layoutType: {
			type: ['Grid'],
			excluded: {
				sort: true,
				filter: true,
			},
		},
	},
}

const statusConfiguration = new statusConfigurationEntity({
	fieldsMeta,
	actionsMeta,
	sectionsMeta,
	screens,
	ActionClass: ContentActions,
	name: 'Status Configuration',
	methodSchemas,
	collectionSchemas,
	entitySchema,
	entityUrlPaths: {
		upsert: 'bpm/ProcessFlow/upsertState/',
		getAll: 'bpm/ProcessFlow/overriddenState',
		getOne: 'bpm/ProcessFlow/overriddenState',
		update: 'bpm/ProcessFlow',
		default: 'bpm/ProcessFlow',
		getTicketStatus: 'getTicketStatus',
		updateRedirectPath: '/tickets/StatusConfiguration/update/',
		createRedirectPath: '/tickets/StatusConfiguration',
	},
	fetchDetailByApi: false,
	appComponent: statusConfigurationContainer,
	title: 'Overridden States',
})

const OverriddenStateListSearchContainer = statusConfiguration.getContainer('ListSearch')
const statusConfigurationCreateContainer = statusConfiguration.getContainer('AppComponent')

export { OverriddenStateListSearchContainer, statusConfigurationCreateContainer }
